'use client';

import { type ReactElement } from 'react';
import { cleanPhoneNumber, cn, formatPhoneNumberForDesign } from '@fshr/shared-react';
import { IconNoAvailableDate } from '../icon';
import getCtaStyles from '../button/get-cta-styles';

const DEFAULT_HEADING = 'Not Available For Your Dates';
const DEFAULT_BODY =
	'This experience is not currently available during your stay. Please contact our concierge.';
const DEFAULT_CHAT_LABEL = 'Chat With Us';

export type Props = {
	headingLabel?: string;
	bodyLabel?: string;
	chatLabel?: string;
	chatHref?: string;
	phoneNumber?: string;
	className?: string;
};

export default function UnavailableBookingWidget({
	headingLabel = DEFAULT_HEADING,
	bodyLabel = DEFAULT_BODY,
	chatLabel = DEFAULT_CHAT_LABEL,
	chatHref,
	phoneNumber,
	className = '',
}: Props): ReactElement {
	const sanitizedPhone = phoneNumber && cleanPhoneNumber(phoneNumber);
	const formattedPhone = phoneNumber && formatPhoneNumberForDesign(phoneNumber);

	return (
		<section
			data-booking-widget
			data-testid="unavailable-booking-widget"
			aria-labelledby="unavailable-booking-widget-title"
			className={cn(
				'flex flex-col rounded-[20px] border border-border-subtle bg-surface-raised shadow-raised',
				className
			)}
		>
			<div className="flex flex-col items-start gap-300 p-300">
				<div className="flex w-full flex-col items-start gap-200">
					<div className="flex w-full flex-row items-center gap-100">
						<span className="shrink-0 text-text-heading" aria-hidden="true">
							<IconNoAvailableDate width={30} height={30} />
						</span>
						<h2 id="unavailable-booking-widget-title" className="text-subtitle1 text-text-heading">
							{headingLabel}
						</h2>
					</div>

					<p className="text-body2 text-text-primary">{bodyLabel}</p>
				</div>

				{(chatHref || sanitizedPhone) && (
					<div className="flex w-full flex-col gap-100">
						{chatHref && (
							<a
								href={chatHref}
								className={getCtaStyles({
									intent: 'filled',
									size: 'small',
									className: 'w-full',
								})}
							>
								{chatLabel}
							</a>
						)}
						{sanitizedPhone && (
							<a
								href={`tel:${sanitizedPhone}`}
								aria-label={`Call ${formattedPhone}`}
								className={getCtaStyles({
									intent: 'outline',
									size: 'small',
									className: 'w-full',
								})}
							>
								{formattedPhone}
							</a>
						)}
					</div>
				)}
			</div>
		</section>
	);
}
